import React, {useEffect} from 'react';
import {GoogleMap, LoadScript, Marker, StandaloneSearchBox} from '@react-google-maps/api';
import {useTranslation} from 'react-i18next';
import {useField} from 'formik';
import * as css from './GoogleMaps.css';
import {useCallback} from 'react';
import useEffectUpdate from '../../hooks/useEffectUpdate';
import {GOOGLE_KEY} from '../../config/config';
import {useRef} from 'react';
import Input from '../input/Input';
import {DEFAULT_LANG} from '../../globalVariables/locals';

const DEFAULT_LOCATION = {lat:31.786961, lng:35.190009};

function GoogleMaps({locationMapName, place, zoom = 17,mapStyle = {width: '100%',height: '100%'},inputStyle = '',locationInputProp={},addressTextKey=null, addressDescKey=null,children,lang=DEFAULT_LANG}) {

    const {t} = useTranslation('global');

    const {placeName,placeText,description}= place;

    const [placeField,,placeHelpers]=useField(placeName);
    const [locationField,,locationHelpers]=useField(locationMapName);

    const getLocationMap = ()=> {
        if(locationField?.value)
            return {lat:locationField?.value?.lat,lng:locationField?.value?.lng};
        else
            return DEFAULT_LOCATION;
    };

    const searchBoxRef= useRef(null);

    const extractCleanTitle = (result) => {
        if(!result?.address_components) return '';

        const getComponent = (type) => {
            const component = result.address_components.find(c => c.types?.includes(type));
            return component?.long_name || component?.short_name || '';
        };

        const street = getComponent('route');
        const streetNumber = getComponent('street_number');
        const city = getComponent('locality') || getComponent('administrative_area_level_1');

        return [street, streetNumber, city].filter(Boolean).join(', ');
    };

    const setValues = (result) =>{
        locationHelpers.setValue({
            lat: parseFloat(result.geometry.location.lat()),
            lng: parseFloat(result.geometry.location.lng()),
            title:result.formatted_address,
            googlePlaceId:result.place_id,
            cleanTitle: extractCleanTitle(result)
        });
    };

    const geocoderFunction = (type) => {
        if(window?.google?.maps?.Geocoder) {
            const geocoder = new window.google.maps.Geocoder({language: lang});
            geocoder.geocode(type, (results, status) => {
                if(status === window.google.maps.GeocoderStatus.OK) {
                    if(results && results.length > 0) {
                        setValues(results[0]);
                    } else {
                        console.error('Geocoder returned no results.');
                    }
                } else {
                    console.error(`Geocoder failed due to: ${status}`);
                }
            });
        } else {
            console.error('Google Maps Geocoder is not available.');
        }
    };

    const onPlacesChanged = () => {
        if(searchBoxRef.current != null) {
            const places = searchBoxRef.current.getPlaces();
            setValues(places[0]);
        }
    };

    const onClickMap = (e) => {
        geocoderFunction({location: e.latLng});
    };

    useEffectUpdate( useCallback(()=>{
        if(placeField?.value)
            geocoderFunction({address: placeField.value});
    }, [placeField?.value]));

    useEffect(()=>{
        placeHelpers.setTouched(locationField?.value?.title && !!placeField?.value);
    },[placeField?.value,locationField?.value.title]);

    return (
        <LoadScript
            googleMapsApiKey = {GOOGLE_KEY}
            libraries={['places']}
            language={lang}
        >
            <css.wrapInput inputStyle={inputStyle}>
                {place && <Input name={placeName} text={placeText} description={description||''} icon='location'/> }
                <StandaloneSearchBox
                    onLoad={(ref)=>searchBoxRef.current = ref}
                    onPlacesChanged={onPlacesChanged}
                >
                    <Input name={`${locationMapName}.title`}
                        text={t(addressTextKey || 'chooseAddress')}
                        description={t(addressDescKey || 'chooseAddressDesc')}
                        icon='address'
                        dataCy='locationTitle'
                        {...locationInputProp}
                    />
                </StandaloneSearchBox>
            </css.wrapInput>
            {children}
            <GoogleMap
                mapContainerStyle={mapStyle}
                center={getLocationMap()||DEFAULT_LOCATION}
                zoom={zoom}
                options={{
                    streetViewControl:false,
                    mapTypeControl:false,

                }}
                onClick={onClickMap}
            >
                <Marker position={getLocationMap()||DEFAULT_LOCATION} />
            </GoogleMap>
        </LoadScript>
    );
}

export default GoogleMaps;
